/**
 * Leviathan Web - API Utility
 * Quasar Prime LLC
 *
 * Fetch wrapper with automatic token refresh on 401 errors
 */

import { API_URL } from '@/config/api';

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry

/**
 * Parse JWT expiration from token without a library
 */
function getTokenExpMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (!parts[1]) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Schedule a proactive token refresh before the access token expires
 */
function scheduleProactiveRefresh(): void {
  // Clear any existing timer
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }

  const token = localStorage.getItem('accessToken');
  if (!token) return;

  const expiresAt = getTokenExpMs(token);
  if (!expiresAt) return;

  const delay = expiresAt - Date.now() - REFRESH_THRESHOLD_MS;

  if (delay <= 0) {
    // Token is about to expire or already past threshold, refresh now
    refreshAccessToken();
    return;
  }

  proactiveRefreshTimer = setTimeout(async () => {
    const newToken = await refreshAccessToken();
    if (newToken) {
      scheduleProactiveRefresh();
    }
  }, delay);
}

/**
 * Start the proactive refresh timer. Call after login or on app mount.
 */
export function startRefreshTimer(): void {
  scheduleProactiveRefresh();
}

/**
 * Stop the proactive refresh timer. Call on logout.
 */
export function stopRefreshTimer(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

/**
 * Refreshes the access token using the stored refresh token
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      return data.accessToken;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }

  return null;
}

/**
 * Fetches with automatic token refresh on 401 errors
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const accessToken = localStorage.getItem('accessToken');

  // Add authorization header
  const headers = new Headers(options.headers);
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Make the request
  let response = await fetch(url, { ...options, headers });

  // If 401, try to refresh and retry once
  if (response.status === 401 && !url.includes('/auth/refresh')) {
    // Deduplicate refresh requests
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken();
    }

    const newToken = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (newToken) {
      // Retry with new token
      headers.set('Authorization', `Bearer ${newToken}`);
      response = await fetch(url, { ...options, headers });
    } else {
      // Refresh failed, clear tokens and redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
  }

  return response;
}

/**
 * JSON POST request with auto-refresh
 */
export async function apiPost<T = unknown>(
  url: string,
  data: unknown
): Promise<{ response: Response; data?: T }> {
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (response.ok) {
    const responseData = await response.json();
    return { response, data: responseData as T };
  }

  return { response };
}

/**
 * JSON PUT request with auto-refresh
 */
export async function apiPut<T = unknown>(
  url: string,
  data: unknown
): Promise<{ response: Response; data?: T }> {
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (response.ok) {
    const responseData = await response.json();
    return { response, data: responseData as T };
  }

  return { response };
}

/**
 * JSON PATCH request with auto-refresh
 */
export async function apiPatch<T = unknown>(
  url: string,
  data: unknown
): Promise<{ response: Response; data?: T }> {
  const response = await apiFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (response.ok) {
    const responseData = await response.json();
    return { response, data: responseData as T };
  }

  return { response };
}

/**
 * JSON GET request with auto-refresh
 */
export async function apiGet<T = unknown>(
  url: string
): Promise<{ response: Response; data?: T }> {
  const response = await apiFetch(url, {
    method: 'GET',
  });

  if (response.ok) {
    const responseData = await response.json();
    return { response, data: responseData as T };
  }

  return { response };
}

/**
 * DELETE request with auto-refresh
 */
export async function apiDelete<T = unknown>(
  url: string
): Promise<{ response: Response; data?: T }> {
  const response = await apiFetch(url, {
    method: 'DELETE',
  });

  if (response.ok) {
    const responseData = await response.json().catch(() => null);
    return { response, data: responseData as T };
  }

  return { response };
}
